import { Box, Grid, Typography } from '@mui/material';
import _ from 'lodash';
import { generatePath, Link } from 'react-router-dom';
import { ApplyProvider } from '../../containers/apply-provider/ApplyProvider';
import { APP_URLS } from '../../routes/urls';
import { moduleCardStyles } from '../../theme/styles/moduleCard';
import { fonts } from '../../theme/typography';
import { ScholarshipListItem } from '../../types/scholarship';
import { getDefaultImageSrc, loadDefaultImageOnError } from '../../utils/image';
import { createExcerptFromArray, formatToNepaliNumber, getFormattedDate } from '../../utils/text';
import { WishlistButton } from '../wishlist-button/WishlistButton';

interface ScholarshipCardProps {
  scholarship: Omit<ScholarshipListItem, 'provider'> & Partial<Pick<ScholarshipListItem, 'provider'>>;
}

export const ScholarshipCard: React.FC<ScholarshipCardProps> = ({ scholarship }) => {
  const renderWishlistButton = (): React.ReactNode => {
    return <WishlistButton sx={moduleCardStyles.wishlistBtn} moduleId={scholarship.id} type="scholarships" />;
  };

  const renderHeader = (): JSX.Element => {
    return (
      <Box sx={moduleCardStyles.header}>
        <Box sx={moduleCardStyles.headerWithImage}>
          <img
            loading="lazy"
            src={scholarship.media || getDefaultImageSrc('scholarship')}
            onError={loadDefaultImageOnError.bind(null, 'scholarship')}
            alt={scholarship.title}
          />
          {renderWishlistButton()}
        </Box>
      </Box>
    );
  };

  const singleScholarshipUrl = generatePath(APP_URLS.singleScholarship, { scholarshipSlug: scholarship.slug });
  const excerptOfProgramsShortName = createExcerptFromArray(
    _.map(scholarship.programs, (program) => program.short_name)
  );

  return (
    <ApplyProvider module="scholarships" applyItems={scholarship}>
      {({ renderApplyBtn, renderApplyDialog }) => (
        <>
          <Box sx={moduleCardStyles.card} component={Link} to={singleScholarshipUrl}>
            {renderHeader()}
            <Box sx={{ p: 3 }}>
              <Typography color="secondary" component="div" variant="medium" sx={{ mb: 1.5 }} fontWeight="Medium">
                {scholarship.scholarship_type.name}
              </Typography>
              <Typography variant="large" component="h6" fontFamily={fonts.heading} fontWeight="Bold">
                {scholarship.title}
              </Typography>
              <Box sx={{ mt: 3 }}>
                <Grid container spacing={0.75} direction="column">
                  <Grid item>
                    <Grid container spacing={0.5}>
                      <Grid item>
                        <Typography variant="medium" color="secondary">
                          Deadline:
                        </Typography>
                      </Grid>
                      <Grid item>
                        <Typography variant="medium" color="warning">
                          {getFormattedDate(scholarship.deadline)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                  {scholarship.programs && scholarship.programs.length > 0 && (
                    <Grid item>
                      <Typography component="div" sx={{ mb: 0.5 }} color="secondary" variant="medium">
                        Applicable Programs:
                      </Typography>
                      <Typography sx={{ color: 'grey.900' }} variant="medium" fontWeight="Bold">
                        {excerptOfProgramsShortName}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>
              <Box sx={{ mt: 4 }}>
                <Grid container spacing={1} justifyContent="space-between" alignItems="center">
                  <Grid item>{renderApplyBtn()}</Grid>
                  {Boolean(scholarship.total_worth) && (
                    <Grid item>
                      <Grid container spacing={0.5} alignItems="center" justifyContent="flex-end">
                        <Grid item>
                          <Typography color="secondary" variant="small" fontWeight="Medium">
                            Nrs.
                          </Typography>
                        </Grid>
                        <Grid item>
                          <Typography variant="large" fontWeight="Bold">
                            {formatToNepaliNumber(parseFloat(scholarship.total_worth as string))}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              </Box>
              {scholarship.provider && (
                <Box sx={{ mt: 2.5 }}>
                  <Typography sx={moduleCardStyles.providerLabel} variant="medium" fontWeight="Medium">
                    {scholarship.provider.name}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
          {renderApplyDialog()}
        </>
      )}
    </ApplyProvider>
  );
};
